// WIRE-IN: call applyMobileControlsHint() from main.ts entry once on startup.
// (BUG-HUD-15) applyMobileControlsHint() also schedules the one-shot mobile
// legend overlay on its first invocation per page; in addition, callers can
// invoke notifyMobileGameplayTick() from the game's per-tick path to defer
// the overlay until the first detected gameplay tick.
//
// Swaps the desktop control hint text in `.menu-controls` for a mobile-friendly
// version when running on a touch device. Reads the replacement string from
// the element's `data-mobile-hint` attribute (set in index.html), so the
// desktop markup remains the source of truth and is shown to keyboard users.
//
// On top of the menu-text swap, BUG-HUD-15 fixes the original "plain text
// hint doesn't map to button positions" problem by showing a one-shot
// labeled-list overlay on first launch. The overlay is keyed off
// BUTTON_DEFS, so adding/removing on-screen buttons in touch-buttons.ts is
// reflected automatically without a second edit. Dismiss persists via the
// localStorage flag `mobile_legend_dismissed` (also the source of truth the
// spec calls out).

import { isMobile } from '../core/device';
import { BUTTON_DEFS, type ButtonDef } from '../input/touch-buttons';

const LEGEND_DISMISSED_KEY = 'mobile_legend_dismissed';
const LEGEND_OVERLAY_ID = 'mobile-legend-overlay';
const LEGEND_BUTTON_ATTR = 'data-mobile-legend-button-id';

/** Process-local guard so the overlay is created at most once per page even
 *  if multiple call sites (page-load wire + gameplay-tick wire) both fire. */
let legendHandled = false;
let legendOrientationCleanup: (() => void) | null = null;

/** Human-readable description for each button id in BUTTON_DEFS. Keyed by id
 *  rather than `key` because two buttons (BOMB and USE) share the same
 *  `interact` input key but mean very different things to the player. New
 *  entries in BUTTON_DEFS get a fallback "<label> button" description until
 *  this map is extended, so the overlay never silently drops a button. */
const BUTTON_DESCRIPTIONS: Record<string, string> = {
  'btn-fire': 'Shoot your current weapon',
  'btn-ads': 'Aim down sights',
  'btn-reload': 'Reload current weapon',
  'btn-jump': 'Jump',
  'btn-crouch': 'Crouch / un-crouch',
  'btn-weapon': 'Switch primary / secondary / knife',
  'btn-ptt': 'Push to talk (voice chat)',
  'btn-scope': 'Toggle scope zoom (AUG / SG553 / AWP)',
  'btn-interact': 'Interact, plant, defuse',
  'btn-buy': 'Open the buy menu (freeze time)',
  'btn-scoreboard': 'Show the scoreboard',
  'btn-grenade': 'Cycle equipped grenade',
  'btn-ping': 'Open the ping wheel',
  'btn-chat': 'Open the chat composer',
  'btn-quickbuy-1': 'Quick-buy slot 1 (freeze time)',
  'btn-quickbuy-2': 'Quick-buy slot 2 (freeze time)',
  'btn-quickbuy-3': 'Quick-buy slot 3 (freeze time)',
  'btn-inspect': 'Inspect your weapon',
  'btn-drop': 'Drop equipped weapon',
  'btn-mapvote': 'Open the casual map-vote panel',
  'btn-bomb': 'Plant / defuse the bomb',
};

/** Friendly screen-position label so players can locate each button visually
 *  even without arrows. Computed once per def from the right/left/top/bottom
 *  percentages declared in BUTTON_DEFS — `right` puts the button on the right
 *  edge, `top` puts it at the top, etc. */
function describeButtonPosition(def: ButtonDef): string {
  const horizontal = def.right !== undefined ? 'right' : def.left !== undefined ? 'left' : 'center';
  const vertical = def.top !== undefined ? 'top' : def.bottom !== undefined ? 'bottom' : 'middle';
  if (horizontal === 'center' && vertical === 'middle') return 'center of HUD';
  if (vertical === 'middle') return `${horizontal} edge`;
  if (horizontal === 'center') return `${vertical} edge`;
  return `${vertical}-${horizontal}`;
}

function formatScreenPosition(horizontal: string, vertical: string): string {
  if (horizontal === 'center' && vertical === 'middle') return 'center of HUD';
  if (vertical === 'middle') return `${horizontal} edge`;
  if (horizontal === 'center') return `${vertical} edge`;
  return `${vertical}-${horizontal}`;
}

function describeActualButtonPosition(def: ButtonDef): string {
  if (typeof document === 'undefined' || typeof window === 'undefined') return describeButtonPosition(def);
  const button = document.getElementById(def.id);
  const rect = button?.getBoundingClientRect();
  if (!rect || rect.width <= 0 || rect.height <= 0) return describeButtonPosition(def);

  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;
  const width = window.innerWidth || document.documentElement.clientWidth || 1;
  const height = window.innerHeight || document.documentElement.clientHeight || 1;
  const horizontal = centerX < width / 3 ? 'left' : centerX > (width * 2) / 3 ? 'right' : 'center';
  const vertical = centerY < height / 3 ? 'top' : centerY > (height * 2) / 3 ? 'bottom' : 'middle';
  return formatScreenPosition(horizontal, vertical);
}

function updateLegendDescription(desc: HTMLElement, def: ButtonDef): void {
  const description = BUTTON_DESCRIPTIONS[def.id] ?? `${def.label} button`;
  desc.textContent = `${description} — ${describeActualButtonPosition(def)}`;
}

function refreshLegendPositions(): void {
  if (typeof document === 'undefined') return;
  const overlay = document.getElementById(LEGEND_OVERLAY_ID);
  if (!overlay) return;
  const byId = new Map(BUTTON_DEFS.map((def) => [def.id, def]));
  const labels = overlay.querySelectorAll<HTMLElement>(`[${LEGEND_BUTTON_ATTR}]`);
  labels.forEach((label) => {
    const id = label.getAttribute(LEGEND_BUTTON_ATTR);
    const def = id ? byId.get(id) : undefined;
    if (def) updateLegendDescription(label, def);
  });
}

function scheduleLegendPositionRefresh(): void {
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(refreshLegendPositions);
  } else {
    refreshLegendPositions();
  }
}

function clearLegendOrientationListeners(): void {
  legendOrientationCleanup?.();
  legendOrientationCleanup = null;
}

function installLegendOrientationListeners(): void {
  if (typeof window === 'undefined') return;
  clearLegendOrientationListeners();

  const refresh = (): void => scheduleLegendPositionRefresh();
  window.addEventListener('orientationchange', refresh);
  window.addEventListener('resize', refresh);

  const media = typeof window.matchMedia === 'function'
    ? window.matchMedia('(orientation: landscape)')
    : null;
  const mediaRefresh = (): void => refresh();
  media?.addEventListener?.('change', mediaRefresh);
  media?.addListener?.(mediaRefresh);

  legendOrientationCleanup = () => {
    window.removeEventListener('orientationchange', refresh);
    window.removeEventListener('resize', refresh);
    media?.removeEventListener?.('change', mediaRefresh);
    media?.removeListener?.(mediaRefresh);
  };
}

/** Internal: has the user already dismissed the legend on a prior session? */
function isLegendDismissed(): boolean {
  try {
    if (typeof localStorage === 'undefined') return false;
    return localStorage.getItem(LEGEND_DISMISSED_KEY) === '1';
  } catch {
    // Private-mode Safari etc. can throw on localStorage access — treat as
    // "not dismissed" so the legend still helps the player, but never crash.
    return false;
  }
}

/** Internal: persist the dismiss flag so we never show the legend again on
 *  this device. Failures (private mode, quota) are swallowed — the overlay
 *  is removed regardless. */
function markLegendDismissed(): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(LEGEND_DISMISSED_KEY, '1');
  } catch {
    // ignore — best effort
  }
}

/** Build the labeled-list overlay DOM. Pulled out so we can unit-test or
 *  re-render without touching the show/dismiss control flow. */
function buildLegendOverlay(): HTMLDivElement {
  const overlay = document.createElement('div');
  overlay.id = LEGEND_OVERLAY_ID;
  Object.assign(overlay.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    background: 'rgba(0, 0, 0, 0.78)',
    color: '#fff',
    zIndex: '2000',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '24px 16px',
    boxSizing: 'border-box',
    fontFamily: 'sans-serif',
    overflowY: 'auto',
  } satisfies Partial<CSSStyleDeclaration>);

  const title = document.createElement('h2');
  title.textContent = 'On-screen controls';
  Object.assign(title.style, {
    margin: '0 0 4px 0',
    fontSize: '20px',
    letterSpacing: '2px',
    color: '#e94560',
  } satisfies Partial<CSSStyleDeclaration>);
  overlay.appendChild(title);

  const subtitle = document.createElement('p');
  subtitle.textContent = 'Find each button by its screen position and label.';
  Object.assign(subtitle.style, {
    margin: '0 0 16px 0',
    fontSize: '12px',
    color: '#bbb',
    textAlign: 'center',
  } satisfies Partial<CSSStyleDeclaration>);
  overlay.appendChild(subtitle);

  const list = document.createElement('ul');
  Object.assign(list.style, {
    listStyle: 'none',
    padding: '0',
    margin: '0 0 16px 0',
    width: '100%',
    maxWidth: '420px',
    fontSize: '13px',
    lineHeight: '1.45',
  } satisfies Partial<CSSStyleDeclaration>);

  for (const def of BUTTON_DEFS) {
    const li = document.createElement('li');
    Object.assign(li.style, {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      padding: '6px 8px',
      borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
    } satisfies Partial<CSSStyleDeclaration>);

    const badge = document.createElement('span');
    badge.textContent = def.label;
    Object.assign(badge.style, {
      display: 'inline-block',
      minWidth: '52px',
      padding: '3px 6px',
      background: 'rgba(233, 69, 96, 0.85)',
      color: '#fff',
      borderRadius: '4px',
      fontWeight: 'bold',
      fontSize: '11px',
      textAlign: 'center',
      flexShrink: '0',
    } satisfies Partial<CSSStyleDeclaration>);
    li.appendChild(badge);

    const desc = document.createElement('span');
    desc.setAttribute(LEGEND_BUTTON_ATTR, def.id);
    updateLegendDescription(desc, def);
    Object.assign(desc.style, {
      color: '#eaeaea',
    } satisfies Partial<CSSStyleDeclaration>);
    li.appendChild(desc);

    list.appendChild(li);
  }
  overlay.appendChild(list);

  const dismiss = document.createElement('button');
  dismiss.textContent = 'Got it — dismiss';
  dismiss.id = 'mobile-legend-dismiss';
  Object.assign(dismiss.style, {
    padding: '12px 28px',
    background: '#e94560',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 'bold',
    letterSpacing: '1px',
    cursor: 'pointer',
    marginBottom: '12px',
  } satisfies Partial<CSSStyleDeclaration>);
  dismiss.addEventListener('click', () => {
    markLegendDismissed();
    clearLegendOrientationListeners();
    overlay.remove();
  });
  overlay.appendChild(dismiss);

  return overlay;
}

/** Mount the legend overlay if (and only if) we're on mobile, the dismiss
 *  flag isn't set, and we haven't already handled it on this page load.
 *  Idempotent — safe to call from multiple sites (page-load wire and
 *  per-tick wire) without double-mounting. */
function maybeShowLegend(): void {
  if (legendHandled) return;
  if (!isMobile()) return;
  if (isLegendDismissed()) {
    legendHandled = true;
    return;
  }
  if (typeof document === 'undefined' || typeof document.body === 'undefined') return;
  // Guard against a duplicate mount if the function somehow runs before the
  // module-level `legendHandled` flips (e.g. if a future caller resets it).
  if (document.getElementById(LEGEND_OVERLAY_ID)) {
    legendHandled = true;
    return;
  }
  const overlay = buildLegendOverlay();
  document.body.appendChild(overlay);
  installLegendOrientationListeners();
  scheduleLegendPositionRefresh();
  legendHandled = true;
}

export function applyMobileControlsHint(): void {
  if (!isMobile()) return;
  const el = document.querySelector('.menu-controls');
  if (el) {
    const hint = el.getAttribute('data-mobile-hint');
    if (hint) {
      el.textContent = hint;
    }
  }
  // BUG-HUD-15: also kick off the one-shot button legend overlay. The
  // overlay self-gates on the dismiss flag, so repeat calls during a
  // session are harmless.
  maybeShowLegend();
}

/** BUG-HUD-15: spec-aligned hook for the first detected gameplay tick.
 *  The game loop (or any caller) can invoke this every tick — it short-
 *  circuits after the first successful render so there is no per-frame
 *  cost. Intentionally not wired from main.ts (caller decides); the
 *  page-load path through applyMobileControlsHint() already covers the
 *  common case. */
export function notifyMobileGameplayTick(): void {
  if (legendHandled) return;
  maybeShowLegend();
}

/** Test hook: reset the module-local one-shot guard. Not exported as part
 *  of the production API surface — vitest imports it directly when it
 *  needs to re-run the overlay path within a single process. */
export function __resetMobileLegendForTests(): void {
  legendHandled = false;
  clearLegendOrientationListeners();
  if (typeof document !== 'undefined' && typeof document.getElementById === 'function') {
    const existing = document.getElementById(LEGEND_OVERLAY_ID);
    existing?.remove?.();
  }
}
